import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { CourseLesson, CourseQuiz, AIAssessment, AIFeedback } from '../types';
import { AIService } from '../utils/aiService';
import { getCleanText } from '../utils/htmlRenderer';

interface InteractiveQuizProps {
  courseId: string;
  lesson: CourseLesson;
  defaultDurationSeconds?: number; // total time limit for the quiz
  onSubmitted?: (result: { score: number; passed: boolean; details: any }) => void;
}

const InteractiveQuiz: React.FC<InteractiveQuizProps> = ({ courseId, lesson, defaultDurationSeconds = 600, onSubmitted }) => {
  const mcqs: CourseQuiz[] = Array.isArray(lesson.quiz) ? lesson.quiz : [];
  const [started, setStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(defaultDurationSeconds);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Answers state
  const [mcqAnswers, setMcqAnswers] = useState<Record<number, number>>({});
  const [shortAnswer, setShortAnswer] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [aiFeedback, setAiFeedback] = useState<AIFeedback | null>(null);

  const totalQuestions = mcqs.length + 1; // +1 for the open-ended question
  const passingScore = 70;

  const openEndedQuestion = useMemo(() => {
    const title = getCleanText(lesson.name || 'This lesson');
    const base = getCleanText(lesson.description || lesson.article_content || '');
    return base
      ? `In your own words, explain the key concept from "${title}". Focus on accuracy and clarity.`
      : `Briefly explain the main idea you learned from "${title}".`;
  }, [lesson]);

  // Timer logic
  useEffect(() => {
    if (!started || submitted) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current as NodeJS.Timeout);
          timerRef.current = null;
          // Auto-submit on time up
          handleSubmit();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [started, submitted]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const handleSelect = (qIndex: number, optionIndex: number) => {
    setMcqAnswers((prev) => ({ ...prev, [qIndex]: optionIndex }));
  };

  const handleSubmit = async () => {
    if (submitted || submitting) return;
    setSubmitting(true);
    try {
      // Score MCQs
      let correct = 0;
      mcqs.forEach((q, i) => {
        if (mcqAnswers[i] === q.correct_ans_index) correct += 1;
      });

      // Grade short answer with AI
      const ai = AIService.getInstance();
      const assessment: AIAssessment = {
        id: `sa_${courseId}_${lesson.id}`,
        courseId,
        lessonId: lesson.id,
        type: 'short_answer',
        question: openEndedQuestion,
        correctAnswer: getCleanText(lesson.description || lesson.article_content || ''),
        explanation: 'Compare student response to lesson core concepts and terminology.',
        difficulty: 'medium',
        tags: ['lesson', 'short_answer'],
        aiGenerated: false,
        createdAt: new Date(),
      };

      let feedback: AIFeedback | null = null;
      if (shortAnswer.trim()) {
        try {
          feedback = await ai.gradeAssessment(assessment, shortAnswer.trim());
        } catch (e) {
          feedback = null;
        }
      }

      const mcqPart = mcqs.length > 0 ? (correct / mcqs.length) * 100 : 0;
      const saPart = feedback ? feedback.score : 0;
      // Weighted: 70% MCQ, 30% short-answer
      const finalScore = Math.round((mcqPart * 0.7) + (saPart * 0.3));

      setScore(finalScore);
      setAiFeedback(feedback);
      setSubmitted(true);
      onSubmitted?.({ score: finalScore, passed: finalScore >= passingScore, details: { mcqCorrect: correct, mcqTotal: mcqs.length, saScore: saPart, aiFeedback: feedback } });
    } finally {
      setSubmitting(false);
    }
  };

  if (!started) {
    return (
      <div className="space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-blue-900">Timed Quiz</div>
              <div className="text-xs text-blue-700">Includes multiple-choice and one short-answer question</div>
            </div>
            <div className="flex items-center text-blue-800 text-sm">
              <ClockIcon className="h-4 w-4 mr-1" /> {formatTime(timeLeft)}
            </div>
          </div>
        </div>
        <button
          onClick={() => setStarted(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm"
        >
          Start Quiz
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div className="text-sm text-gray-700">Answer all questions before time runs out</div>
        <div className={`flex items-center text-sm ${timeLeft <= 30 ? 'text-red-600' : 'text-gray-700'}`}>
          <ClockIcon className="h-4 w-4 mr-1" /> {formatTime(timeLeft)}
        </div>
      </div>

      {/* MCQs */}
      {mcqs.map((q, qi) => (
        <div key={qi} className="border border-gray-200 rounded-lg p-3">
          <div className="text-sm font-medium text-gray-900 mb-2">{qi + 1}. {getCleanText(q.question)}</div>
          <div className="space-y-2">
            {q.options.map((opt: string, oi: number) => (
              <label key={oi} className={`flex items-center p-2 border rounded cursor-pointer ${mcqAnswers[qi] === oi ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name={`q-${qi}`}
                  value={oi}
                  checked={mcqAnswers[qi] === oi}
                  onChange={() => handleSelect(qi, oi)}
                  className="mr-2 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{getCleanText(opt)}</span>
              </label>
            ))}
          </div>
          {submitted && (
            <div className="mt-2 p-2 rounded bg-gray-50">
              <div className="flex items-center gap-2">
                {mcqAnswers[qi] === q.correct_ans_index ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircleIcon className="h-4 w-4 text-red-600" />
                )}
                <span className={`text-xs font-medium ${mcqAnswers[qi] === q.correct_ans_index ? 'text-green-700' : 'text-red-700'}`}>
                  {mcqAnswers[qi] === q.correct_ans_index ? 'Correct' : `Incorrect. Correct: ${getCleanText(q.options[q.correct_ans_index])}`}
                </span>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Short Answer */}
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-900 mb-2">{mcqs.length + 1}. {openEndedQuestion}</div>
        <textarea
          value={shortAnswer}
          onChange={(e) => setShortAnswer(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md text-sm min-h-[100px]"
          placeholder="Type your answer here..."
          disabled={submitted}
        />
        {submitted && aiFeedback && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-md p-3">
            <div className="text-sm font-semibold text-green-900">AI Feedback</div>
            <div className="text-xs text-green-800">Score: {aiFeedback.score}%</div>
            <div className="text-sm text-green-900 mt-1">{aiFeedback.feedback}</div>
            {aiFeedback.improvementSuggestions?.length ? (
              <ul className="list-disc pl-5 text-sm text-green-900 mt-1">
                {aiFeedback.improvementSuggestions.map((s, i) => (<li key={i}>{s}</li>))}
              </ul>
            ) : null}
          </div>
        )}
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-600">{Object.keys(mcqAnswers).length}/{mcqs.length} MCQs answered • 1 short-answer</div>
        <button
          onClick={handleSubmit}
          disabled={submitted || submitting}
          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm disabled:opacity-50"
        >
          {submitted ? 'Submitted' : submitting ? 'Submitting...' : 'Submit Quiz'}
        </button>
      </div>

      {submitted && score !== null && (
        <div className={`mt-2 p-3 rounded-md ${score >= passingScore ? 'bg-green-50 border border-green-200 text-green-900' : 'bg-yellow-50 border border-yellow-200 text-yellow-900'}`}>
          <div className="text-sm font-semibold">Final Score: {score}%</div>
          <div className="text-xs">{score >= passingScore ? 'Passed' : 'Keep practicing and review the materials.'}</div>
        </div>
      )}
    </div>
  );
};

export default InteractiveQuiz;


